/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type JSX, useCallback, useMemo } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@/hooks/use-navigate';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getPaginationItems } from '@/lib/pagination-utils';
import { cn } from '@/lib/utils';

function PaginationEllipsis(): JSX.Element {
    const { t } = useTranslation('category');

    return (
        <span className="inline-flex size-9 items-center justify-center gap-1" aria-hidden>
            <span className="size-1 rounded-full bg-foreground" />
            <span className="size-1 rounded-full bg-foreground" />
            <span className="size-1 rounded-full bg-foreground" />
            <span className="sr-only">{t('pagination.morePages')}</span>
        </span>
    );
}

export default function CategoryPagination({
    limit,
    offset,
    total,
}: {
    limit: number;
    offset: number;
    total: number;
}): JSX.Element | null {
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const { t } = useTranslation('category');
    const isPending = navigation.state !== 'idle';

    /**
     * Optimistic offset derived from the in-flight navigation target.
     *
     * While a navigation is pending, `navigation.location` holds the target location, allowing us to read the
     * intended offset param immediately. Once the navigation settles, we fall back to the server-provided value.
     */
    const effectiveOffset = navigation.location
        ? Number(new URLSearchParams(navigation.location.search).get('offset') || offset)
        : offset;

    const totalPages = Math.ceil(total / limit);
    const current = Math.floor(effectiveOffset / limit) + 1;
    const pageNumbers = useMemo(() => getPaginationItems(totalPages, current), [totalPages, current]);

    const navigatePage = useCallback(
        (page: number) => {
            const params = new URLSearchParams(location.search);
            params.set('offset', String((page - 1) * limit));
            void navigate({
                ...location,
                search: `?${params.toString()}`,
            });
        },
        [location, navigate, limit]
    );

    if (totalPages <= 1) {
        return null;
    }

    return (
        <div className="flex justify-center">
            <nav
                className={cn(
                    'flex h-9 items-center gap-4 text-sm font-normal leading-5 tracking-[-0.15px] text-primary',
                    isPending && 'pointer-events-none opacity-50 transition-opacity'
                )}
                aria-label={t('pagination.label')}>
                <button
                    type="button"
                    className={cn(
                        'inline-flex h-9 cursor-pointer items-center gap-2.5 rounded-none bg-transparent p-0 outline-none',
                        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                        'disabled:cursor-not-allowed disabled:text-muted-foreground'
                    )}
                    onClick={() => void navigatePage(current - 1)}
                    disabled={current <= 1}
                    aria-label={t('pagination.previousPage')}>
                    <ChevronLeft className="size-4 shrink-0 stroke-foreground" aria-hidden />
                    <span>{t('pagination.previous')}</span>
                </button>

                {pageNumbers.map((item) =>
                    typeof item === 'object' ? (
                        <PaginationEllipsis key={`ellipsis-${item.key}`} />
                    ) : (
                        <button
                            key={item}
                            type="button"
                            onClick={() => void navigatePage(item)}
                            disabled={current === item}
                            className={cn(
                                'inline-flex size-9 cursor-pointer items-center justify-center rounded-none p-0 outline-none',
                                'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                                'disabled:cursor-default',
                                current === item
                                    ? 'border border-border-subtle bg-background shadow-xs'
                                    : 'border border-transparent bg-transparent'
                            )}
                            aria-label={t('pagination.page', { number: item })}
                            aria-current={current === item ? 'page' : undefined}>
                            {item}
                        </button>
                    )
                )}

                <button
                    type="button"
                    className={cn(
                        'inline-flex h-9 cursor-pointer items-center gap-2.5 rounded-none bg-transparent p-0 outline-none',
                        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                        'disabled:cursor-not-allowed disabled:text-muted-foreground'
                    )}
                    onClick={() => void navigatePage(current + 1)}
                    disabled={current === totalPages}
                    aria-label={t('pagination.nextPage')}>
                    <span>{t('pagination.next')}</span>
                    <ChevronRight className="size-4 shrink-0 stroke-foreground" aria-hidden />
                </button>
            </nav>
        </div>
    );
}
